/*
 * Broadcast Theme
 *
 * Use this file to add custom Javascript to Broadcast.  Keeping your custom
 * Javascript in this fill will make it easier to update Broadcast. In order
 * to use this file you will need to open layout/theme.liquid and uncomment
 * the custom.js script import line near the bottom of the file.
 */

class SwiperVariantSync {
  constructor(swiperInstance, options = {}) {
    this.swiper = swiperInstance;
    this.options = {
      selectorPrefix: options.selectorPrefix || ".swiper-product-gallery",
      ...options,
    };
    this._init();
  }

  _init() {
    this._syncInitialVariant();
    this._initVariantChangeListener();
  }

  _syncInitialVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get("variant");

    const selectedInput = document.querySelector(
      'input[name="id"]:checked, select[name="id"]',
    );
    const variantId = variantIdFromUrl || selectedInput?.value;

    if (variantId) {
      const variant = this._getVariantById(variantId);
      if (variant?.featured_media?.id) {
        this._changeSlideByMediaId(variant.featured_media.id);
      }
    }
  }

  _initVariantChangeListener() {
    document.addEventListener("variant:change", (event) => {
      const variant = event.detail?.variant;
      if (variant?.featured_media?.id) {
        this._changeSlideByMediaId(variant.featured_media.id);
      }
    });

    const variantSelects = document.querySelectorAll(
      'select[name="id"], input[name="id"]',
    );
    variantSelects.forEach((select) => {
      select.addEventListener("change", (e) => {
        const variantId = e.target.value;
        const variant = this._getVariantById(variantId);
        if (variant?.featured_media?.id) {
          this._changeSlideByMediaId(variant.featured_media.id);
        }
      });
    });
  }

  _changeSlideByMediaId(mediaId) {
    const targetMediaId = String(mediaId);
    let targetIndex = -1;

    for (let i = 0; i < this.swiper.slides.length; i++) {
      const slide = this.swiper.slides[i];
      const isDuplicate = slide.classList.contains("swiper-slide-duplicate");
      const dataMediaId = slide.dataset.mediaId;
      const swiperSlideIndex = slide.dataset.swiperSlideIndex;

      if (dataMediaId && !isDuplicate) {
        const parts = dataMediaId.split("-");
        const slideMediaId = parts[parts.length - 1];

        if (slideMediaId === targetMediaId) {
          targetIndex = swiperSlideIndex ? parseInt(swiperSlideIndex) : i;
          break;
        }
      }
    }

    if (targetIndex !== -1) {
      this.swiper.slideToLoop(targetIndex, 300);
    }
  }

  _getVariantById(variantId) {
    const productJson = document.querySelector("[data-product-json]");
    if (productJson) {
      const product = JSON.parse(productJson.textContent);
      return product.variants.find((v) => v.id == variantId);
    }
    return null;
  }
}

class AccordionSeparated {
  constructor(options = {}) {
    this.options = {
      triggerSelector:
        options.triggerSelector || "[data-separated-accordion-trigger]",
      contentSelector:
        options.contentSelector || "[data-separated-accordion-content]",
      activeClass: options.activeClass || "is-separated-accordion-active",
      namespace: options.namespace || "separated-accordion",
      ...options,
    };
    this._init();
  }

  _init() {
    this.triggers = document.querySelectorAll(this.options.triggerSelector);

    this.triggers.forEach((trigger) => {
      const targetId = trigger.dataset.separatedAccordionTrigger;
      const content = document.querySelector(
        `[data-separated-accordion-content="${targetId}"]`,
      );

      if (content) {
        this._setupAccordion(trigger, content);
      }
    });
  }

  _setupAccordion(trigger, content) {
    const isOpen = trigger.classList.contains(this.options.activeClass);
    const inner = content.querySelector(".separated-accordion-content__inner");
    const maxContentHeight =
      getComputedStyle(content).getPropertyValue("--max-content-height") ||
      "400px";
    const actualHeight = inner
      ? Math.min(inner.scrollHeight + 40, parseInt(maxContentHeight))
      : content.scrollHeight;

    content.style.maxHeight = isOpen ? `${actualHeight}px` : "0";
    content.style.transition = "max-height 0.3s ease, opacity 0.3s ease";

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      this._toggle(trigger, content);
    });
  }

  _toggle(trigger, content) {
    const isActive = trigger.classList.contains(this.options.activeClass);
    const inner = content.querySelector(".separated-accordion-content__inner");
    const maxContentHeight =
      getComputedStyle(content).getPropertyValue("--max-content-height") ||
      "400px";
    const actualHeight = inner
      ? Math.min(inner.scrollHeight + 40, parseInt(maxContentHeight))
      : content.scrollHeight;

    if (isActive) {
      content.classList.remove("transition-complete");
      content.style.maxHeight = "0";
      trigger.classList.remove(this.options.activeClass);
      content.classList.remove(this.options.activeClass);
    } else {
      trigger.classList.add(this.options.activeClass);
      content.classList.add(this.options.activeClass);
      content.style.maxHeight = `${actualHeight}px`;

      setTimeout(() => {
        if (content.classList.contains(this.options.activeClass)) {
          content.classList.add("transition-complete");
        }
      }, 300);
    }
  }

  toggle(id) {
    const trigger = document.querySelector(
      `[data-separated-accordion-trigger="${id}"]`,
    );
    const content = document.querySelector(
      `[data-separated-accordion-content="${id}"]`,
    );

    if (trigger && content) {
      this._toggle(trigger, content);
    }
  }
}

class VariantQuickView {
  constructor() {
    this.init();
  }

  init() {
    if (document.readyState !== "loading") {
      this.handleModalCreation();
    }

    document.addEventListener("DOMContentLoaded", () =>
      this.handleModalCreation(),
    );
    document.addEventListener("shopify:section:load", () =>
      this.handleModalCreation(),
    );
  }

  handleModalCreation() {
    const quickViewButtons = document.querySelectorAll(
      "[data-quick-add-modal-handle]",
    );

    quickViewButtons.forEach((button) => {
      if (button.dataset.variantListenerAttached) return;

      const variantId = button.getAttribute("data-variant-id");
      const productHandle = button.getAttribute("data-quick-add-modal-handle");

      button.addEventListener("click", () => {
        if (!variantId || !productHandle) return;

        this.waitForModalContent(variantId, productHandle);
      });

      button.dataset.variantListenerAttached = "true";
    });
  }

  async waitForModalContent(
    variantId,
    productHandle,
    attempts = 0,
    maxAttempts = 30,
  ) {
    const openModal = document.querySelector("[data-quick-add-modal][open]");

    if (!openModal && attempts < maxAttempts) {
      setTimeout(
        () =>
          this.waitForModalContent(
            variantId,
            productHandle,
            attempts + 1,
            maxAttempts,
          ),
        100,
      );
      return;
    }

    if (!openModal) return;

    const allOptions = openModal.querySelectorAll('input[type="radio"]');

    if (allOptions.length === 0 && attempts < maxAttempts) {
      setTimeout(
        () =>
          this.waitForModalContent(
            variantId,
            productHandle,
            attempts + 1,
            maxAttempts,
          ),
        100,
      );
      return;
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const product = await response.json();
      const targetVariant = product.variants.find(
        (v) => v.id.toString() === variantId.toString(),
      );

      if (!targetVariant) return;

      this.selectOptions(openModal, targetVariant);
      this.updateProductUrl(openModal, productHandle, variantId);
    } catch (error) {
      console.error("Error fetching product data:", error);
    }
  }

  selectOptions(modal, targetVariant) {
    const optionsToSelect = [
      targetVariant.option1,
      targetVariant.option2,
      targetVariant.option3,
    ].filter((opt) => opt !== null);

    optionsToSelect.forEach((optionValue) => {
      const input = modal.querySelector(
        `input[type="radio"][value="${optionValue}"]`,
      );
      if (input && !input.checked) {
        input.click();
      }
    });
  }

  updateProductUrl(modal, productHandle, variantId) {
    const detailsLink = modal.querySelector(".quick-add__details a");
    if (detailsLink) {
      detailsLink.href = `/products/${productHandle}?variant=${variantId}`;
    }
  }
}

class App {
  constructor() {
    this._init();
  }

  _init() {
    this._createLenis();
    this._renderLenis();
    this._getLayoutHeights();
    this._initHeaderSticky();
    this._getProductContentTop();
    this._initHideArtistLogo();
    this._initSwiper();
    this._initCartToggle();
    this._initExternalLinks();
    this._initModals();
    this._initSeparatedAccordions();
    this._initCartJumpAnimation();
    this._initVariantQuickView();
  }

  _createLenis() {
    this.lenis = new Lenis({
      lerp: 0.05,
      easing: (t) => 1 - Math.pow(1 - t, 5),
    });
  }

  _renderLenis(time) {
    this.lenis.raf(time);
    requestAnimationFrame(this._renderLenis.bind(this));
  }

  _getLayoutHeights() {
    const elements = [
      {
        selector: ".bg-logo-image__wrapper.mobile",
        variable: "--artist-logo-height",
        offset: 10,
      },
      {
        selector: ".header-bottom--nav--item.cart--item",
        variable: "--cart-icon-height",
        offset: 10,
      },
      {
        selector: ".product__feat-images",
        variable: "--feat-images-height",
        offset: 0,
      },
    ];

    elements.forEach(({ variable, selector, offset }) => {
      const element = document.querySelector(selector);
      if (element) {
        document.documentElement.style.setProperty(
          variable,
          `${element.offsetHeight + (offset ? offset : 0)}px`,
        );
      }
    });
  }

  _getProductContentTop() {
    const product_content = document.querySelector(".product__content");
    if (product_content) {
      document.documentElement.style.setProperty(
        "--product-content-top",
        `${product_content.offsetTop}px`,
      );
    }
  }

  _initHeaderSticky() {
    const header = document.querySelector(
      "header-component[data-header-sticky]",
    );
    if (!header) return;

    const offset = header.offsetTop;

    window.addEventListener("scroll", () => {
      const shouldStick = window.scrollY > offset;
      const isStuck = header.classList.contains("js__header__stuck");

      if (shouldStick && !isStuck) {
        header.classList.add("js__header__stuck");
      }

      if (!shouldStick && isStuck) {
        header.classList.remove("js__header__stuck");
      }
    });
  }

  _renderSpline() {
    import(window.SPLINE_ASSETS.runtime).then(({ Application }) => {
      const app = new Application(document.getElementById("canvas3d"), {
        wasmPath: "/",
      });
      app.load(window.SPLINE_ASSETS.scene);
    });
  }

  _initSwiper() {
    const singleElement = document.querySelector(
      ".swiper-product-gallery--single",
    );
    const centeredElement = document.querySelector(
      ".swiper-product-gallery--centered",
    );

    if (singleElement) {
      this.productSwiperSingle = new Swiper(".swiper-product-gallery--single", {
        loop: true,
        speed: 600,
        effect: "fade",
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
      });

      new SwiperVariantSync(this.productSwiperSingle, {
        selectorPrefix: ".swiper-product-gallery--single",
      });
    }

    if (centeredElement) {
      this.productSwiperCentered = new Swiper(
        ".swiper-product-gallery--centered",
        {
          slidesPerView: 2,
          spaceBetween: 25,
          loop: true,
          centeredSlides: true,
          allowTouchMove: false,
          simulateTouch: false,
          breakpoints: {
            750: {
              slidesPerView: "auto",
              spaceBetween: 30,
            },
          },
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
        },
      );

      new SwiperVariantSync(this.productSwiperCentered, {
        selectorPrefix: ".swiper-product-gallery--centered",
      });
    }

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", () => {
        this._destroySwiper();
        this._initSwiper();
      });

      document.addEventListener("shopify:section:reorder", () => {
        this._destroySwiper();
        this._initSwiper();
      });
    }
  }

  _destroySwiper() {
    if (this.productSwiperSingle) {
      this.productSwiperSingle.destroy(true, true);
      this.productSwiperSingle = null;
    }

    if (this.productSwiperCentered) {
      this.productSwiperCentered.destroy(true, true);
      this.productSwiperCentered = null;
    }
  }

  _initSeparatedAccordions() {
    this.separatedAccordion = new AccordionSeparated({
      triggerSelector: "[data-separated-accordion-trigger]",
      contentSelector: "[data-separated-accordion-content]",
      activeClass: "is-separated-accordion-active",
      namespace: "separated-accordion",
    });

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", () => {
        this.separatedAccordion = new AccordionSeparated();
      });
    }
  }

  _initExternalLinks() {
    const links = [...document.querySelectorAll("a:not(.cookie-policy)")];
    links.forEach((link) => {
      if (link.host !== window.location.hostname) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noreferrer noopener");
      }
    });
  }

  _openToggle(el) {
    if (el) {
      el.setAttribute("aria-hidden", "false");
    }
  }

  _closeToggle(el) {
    if (el) {
      el.setAttribute("aria-hidden", "true");
    }
  }

  _initCartToggle() {
    if (theme.settings.cartType !== "drawer") return;

    const drawer = document.querySelector("cart-drawer");
    if (!drawer) return;

    let isHandling = false;

    const toggles = document.querySelectorAll("[data-cart-toggle]");

    toggles.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();

        if (drawer.classList.contains("is-open")) return;
        if (isHandling) return;

        isHandling = true;

        drawer.dispatchEvent(new CustomEvent("theme:cart-drawer:show"));

        this._openToggle();

        setTimeout(() => {
          isHandling = false;
        }, 500);
      });
    });

    let wasOpen = false;

    const observer = new MutationObserver(() => {
      const isOpen = drawer.classList.contains("is-open");

      if (wasOpen && !isOpen) {
        this._closeToggle();
      }

      wasOpen = isOpen;
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  _initHideArtistLogo() {
    const logo = document.querySelector(".bg-logo-image__wrapper.desktop");
    const triggerSection = document.querySelector(".product__feat-images");

    if (!logo || !triggerSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            logo.classList.add("hide-element");
          } else if (entry.boundingClientRect.top > 0) {
            logo.classList.remove("hide-element");
          }
        });
      },
      {
        rootMargin: `0px 0px -${window.innerHeight - 1}px 0px`,
        threshold: 0,
      },
    );

    observer.observe(triggerSection);
  }

  _initModals() {
    const triggers = document.querySelectorAll("[data-modal-trigger]");

    triggers.forEach((btn) => {
      const id = btn.dataset.modalId;
      const modal = document.getElementById(id);
      if (!modal) return;

      const closeElements = [
        modal.querySelector(".modal-overlay"),
        modal.querySelector("[data-modal-close]"),
      ];

      btn.addEventListener("click", () => {
        const isOpen = modal.getAttribute("aria-hidden") === "false";

        isOpen ? this._closeToggle(modal) : this._openToggle(modal);
      });

      closeElements.forEach((el) => {
        el?.addEventListener("click", () => {
          this._closeToggle(modal);
        });
      });

      this._initModalLinks(modal);
    });
  }

  _initModalLinks(modal) {
    const modalContent = modal.querySelector(".modal-content");
    if (!modalContent) return;

    modalContent.addEventListener("click", (e) => {
      const link = e.target.closest("a");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href) return;

      const urlParts = href.split("/");
      const pageHandle =
        urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2];

      const triggers = document.querySelectorAll("[data-modal-trigger]");
      let targetModalId = null;

      triggers.forEach((trigger) => {
        const triggerText = trigger.textContent.trim().toLowerCase();
        const linkText = link.textContent.trim().toLowerCase();

        if (
          triggerText === linkText ||
          pageHandle.includes(triggerText.replace(/\s+/g, "-"))
        ) {
          targetModalId = trigger.dataset.modalId;
        }
      });

      if (targetModalId) {
        e.preventDefault();

        const targetModal = document.getElementById(targetModalId);
        if (targetModal) {
          this._closeToggle(modal);

          setTimeout(() => {
            this._openToggle(targetModal);
          }, 300);
        }
      }
    });
  }

  _initCartJumpAnimation() {
    document.addEventListener("theme:cart:change", () => {
      this._triggerCartJump();
    });
  }

  _triggerCartJump() {
    const cartIcons = document.querySelectorAll(".header__cart__status");

    cartIcons.forEach((cartIcon) => {
      cartIcon.classList.remove("cart-jump");
      void cartIcon.offsetWidth;
      cartIcon.classList.add("cart-jump");

      setTimeout(() => {
        cartIcon.classList.remove("cart-jump");
      }, 600);
    });
  }

  _initVariantQuickView() {
    new VariantQuickView();
  }
}

(function () {
  new App();
})();
